import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { X9Content, X9ContentSnapshot } from '../../../../../../domain';
import { X9ContentStateStore } from '../../../common/X9ContentStateStore';
import { ReactNodeGenerator, isIOS } from '../../../../../../lib/helpers';
import { SyncedVideoModel } from '../../models';
import { VideoProgress } from '../common/VideoProgress';
import { StudentSyncedVideoStore } from './StudentSyncedVideoStore';
import { VideoElementStore } from '../common/VideoElementStore';
import { WithTranslation, withTranslation } from 'react-i18next';

interface Props {
  x9: X9Content | X9ContentSnapshot,
  store: X9ContentStateStore
  lessonType?: string | undefined
}

function* trackedStudentProgress(store: StudentSyncedVideoStore): ReactNodeGenerator {
  for (let [clientId, progressStore] of store.trackedStudents.entries())
    yield <VideoProgress key={clientId}
      store={progressStore}
      className="student-progress" />;
}

@observer
class StudentPlayButton extends React.Component<{ store: VideoElementStore }> {
  render() {
    const store = this.props.store;
    const classname = store.paused || store.ended
      ? 'fa fa-play'
      : 'fa fa-pause';
    return (
      <button type="button" className="btn btn-outline-primary"
        onClick={store.togglePlay}>
        <span className={classname} />
      </button>);
  }
}

interface IProps extends WithTranslation {
  store: VideoElementStore
}

@observer
class StudentIOSOverlayComponent extends React.Component<IProps> {
  render() {
    const store = this.props.store;
    return (
      <div onClick={store.toggleVolume} className='student-ios-video-overlay'>
        <i className="far fa-hand-point-up"></i>
        <p>{this.props.t('classroom.video.ios-show-unmute')}</p>
      </div>
    );
  }
}

const StudentIOSOverlay = withTranslation()(StudentIOSOverlayComponent);

@observer
class StudentSyncedVideoControls extends React.Component<{ store: StudentSyncedVideoStore, lessonType: string | undefined }> {
  render() {
    const store = this.props.store;
    const lessonType = this.props.lessonType;

    return (
      <div className="row no-gutters">
        <div className="col-auto playerBlockButton">
          {(lessonType !== 'Standard' && lessonType !== 'Demo') && <StudentPlayButton store={store.video} />}
        </div>
        <div className="col playerBlock">
          <VideoProgress className="teacher-progress" store={store.progress}
            updateVideo={store.video} />
          {
            Array.from(trackedStudentProgress(store))
          }
        </div>
      </div>);
  }
}

@observer
export class StudentSyncedVideo extends React.Component<Props> {
  readonly videoRef = React.createRef<HTMLVideoElement>();

  readonly store: StudentSyncedVideoStore;
  readonly lessonType: string | undefined;

  readonly isIOS = isIOS();

  constructor(props: Readonly<Props>) {
    super(props);
    const model = JSON.parse(props.x9.Contents) as SyncedVideoModel;
    this.store = new StudentSyncedVideoStore(props.x9.Id, model, props.store);
    this.lessonType = props.lessonType;
  }

  render() {
    const store = this.store;

    const handleError = () => {
      store.video.setIsVideoError(true);
    }

    return (
      <div className="student-synced-video">
        <video className="synced-video"
          ref={this.videoRef}
          src={store.model.videoUrl}
          controls={false}
          autoPlay={false}
          loop={false}
          preload="auto"
          muted={this.isIOS}
          playsInline={this.isIOS}
          onError={handleError} />
        {store.video.isVideoError && <p className="getUrlSourceError">Video not found</p>}
        <StudentSyncedVideoControls store={store} lessonType={this.lessonType} />
        {this.isIOS && this.store.video.muted && <StudentIOSOverlay store={store.video} />}
      </div>);
  }

  componentDidMount() {
    this.store.video.setElement(this.videoRef.current!);
  }

  componentWillUnmount() {
    this.store.video.pause();
    this.store.video.setCurrentTime(0);
    this.store.video.setIsVideoError(false);
    this.store.dispose();
  }
}
